import React, { useState } from "react";
import TribunaisDialog from "./TribunaisDialog";
import { formatCpfCnpj } from "../../../utils/formatCpfCnpj";

/**
 * Componente de tabela para exibir a lista de nomes
 */
const NomesTable = ({
  nomes,
  selectedItems,
  handleSelectItem,
  handleSelectAll,
  onDelete,
  hasMore,
  loadingMore,
  onLoadMore,
  total,
  semFranquia = false
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tribunaisDialogOpen, setTribunaisDialogOpen] = useState(false);
  const [tribunaisSelecionados, setTribunaisSelecionados] = useState("");

  const handleDelete = (id) => {
    setConfirmDelete(id);
  };

  const formatarData = (dataString) => {
    if (!dataString) return "-";
    
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };


  const confirmDeleteNome = async (id) => {
    const success = await onDelete(id);
    if (success) {
      setConfirmDelete(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  // Função para parsear a lista de tribunais (JSON ou string separada por vírgula)
  const parseTribunais = (value) => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      // fallback: split por vírgula e limpar aspas/colchetes
    }
    return value
      .replace(/[\[\]"]/g, "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  };

  // Função para exibir no máximo 3 tribunais e adicionar "Ver mais"
  const renderTribunais = (texto) => {
    if (!texto) return "-";

    const tribunais = parseTribunais(texto);
    if (tribunais.length === 0) return "-";

    const limite = 6;
    const visiveis = tribunais.slice(0, limite);
    const restantes = tribunais.length - limite;

    return (
      <div className="tribunais-col">
        <span className="tribunais-visiveis">
          {visiveis.join(", ")}
        </span>
        {restantes > 0 && (
          <span
            className="ver-mais-tribunais"
            onClick={() => {
              setTribunaisSelecionados(texto);
              setTribunaisDialogOpen(true);
            }}
            title={`Ver todos os ${tribunais.length} tribunais`}
          >
            +{restantes} <strong>Ver mais</strong>
          </span>
        )}
      </div>
    );
  };

  // Função para renderizar lista de documentos (um por linha ou separados por vírgula/\n)
  const renderDocumentos = (valor) => {
    if (!valor) return "-";
    const docs = valor
      .split(/[\n,]+/)
      .map((d) => d.trim())
      .filter(Boolean);
    if (docs.length === 0) return "-";
    return (
      <div className="flex flex-col gap-0.5">
        {docs.map((doc, i) => (
          <span key={i} className="text-xs font-mono">{formatCpfCnpj(doc)}</span>
        ))}
      </div>
    );
  };

  // Função para estilizar o status do provedor
  const renderStatusProvedor = (status) => {
    if (!status) return "-";
    const statusNormalizado = status.toUpperCase();
    const isPendente = statusNormalizado === "PENDENTE";
    // Sem franquia contratada o nome não é enviado ao provedor; "Pendente" levaria a esperar indefinidamente
    if (isPendente && semFranquia) {
      return (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
          title="Franquia de distribuição não contratada. O nome será ativado após a contratação."
        >
          Aguardando franquia
        </span>
      );
    }
    const cls =
      statusNormalizado === "ATIVO" ? "bg-emerald-100 text-emerald-700" :
      isPendente ? "bg-amber-100 text-amber-700" :
      "bg-slate-100 text-slate-600";
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
  };

  const thCls = "px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider";
  const tdCls = "px-4 py-3 text-sm text-foreground";

  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-border">
            <tr>
              <th className={thCls}>
                <input
                  type="checkbox"
                  checked={selectedItems.length === nomes.length && nomes.length > 0}
                  onChange={handleSelectAll}
                  title="Selecionar todos"
                  className="rounded"
                />
              </th>
              <th className={thCls}>Data</th>
              <th className={thCls}>Nome</th>
              <th className={thCls}>CPF/CNPJ / Documentos</th>
              <th className={thCls}>Polo</th>
              <th className={thCls}>Tribunais</th>
              <th className={thCls}>Status</th>
              <th className={thCls}>Monitoramento</th>
              <th className={thCls}>Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {nomes.length > 0 ? (
              nomes.map((cliente) => (
                <tr
                  key={cliente.id}
                  className={`hover:bg-slate-50 transition-colors ${
                    selectedItems.includes(cliente.id) ? "bg-blue-50" : ""
                  }`}
                >
                  <td className={tdCls}>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(cliente.id)}
                      onChange={() => handleSelectItem(cliente.id)}
                      className="rounded"
                    />
                  </td>
                  <td className={tdCls}>{formatarData(cliente.data_criacao)}</td>
                  <td className={`${tdCls} font-medium`}>{cliente.nome}</td>
                  <td className={tdCls}>
                    {cliente.documentos
                      ? renderDocumentos(cliente.documentos)
                      : cliente.cpf_cnpj ? formatCpfCnpj(cliente.cpf_cnpj) : "-"}
                  </td>
                  <td className={tdCls}>{cliente.polo || "-"}</td>
                  <td className={`${tdCls} max-w-[200px]`}>{renderTribunais(cliente.lista_tribunais)}</td>
                  <td className={tdCls}>{cliente.status}</td>
                  <td className={tdCls}>{renderStatusProvedor(cliente.status_provedor)}</td>
                  <td className={tdCls}>
                    {confirmDelete === cliente.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          className="px-2 py-1 text-xs rounded bg-red-500 text-white hover:bg-red-600"
                          onClick={() => confirmDeleteNome(cliente.id)}
                        >Sim</button>
                        <button
                          className="px-2 py-1 text-xs rounded border border-border hover:bg-slate-50"
                          onClick={cancelDelete}
                        >Não</button>
                      </div>
                    ) : (
                      <button
                        className="text-muted-foreground hover:text-red-600 transition-colors"
                        onClick={() => handleDelete(cliente.id)}
                        title="Excluir"
                      >
                        🗑️
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nenhum termo de pesquisa encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {nomes.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-slate-50">
          <span className="text-xs text-muted-foreground">Exibindo {nomes.length} de {total} registros</span>
          {hasMore && (
            <button
              className="px-4 py-2 text-sm font-medium rounded-md border border-border hover:bg-white transition-colors disabled:opacity-50"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Carregando..." : "Carregar mais"}
            </button>
          )}
        </div>
      )}
      
      {tribunaisDialogOpen && (
        <TribunaisDialog 
          open={tribunaisDialogOpen} 
          onClose={() => setTribunaisDialogOpen(false)} 
          tribunais={tribunaisSelecionados}
        />
      )}
    </div>
  );
};

export default NomesTable;
